/**
 * Work Mode — row shapes for the tables created in migration 116.
 *
 * None of these are in the generated `database.ts` stub, so the hooks use
 * the `from('table' as never)` + `as unknown as T` convention that the
 * inventory feature established.
 */

export type WorkAuthMethod = 'pin' | 'badge' | 'self';
export type WorkEndReason = 'manual' | 'idle' | 'switched' | 'manager' | 'stale';

export interface WorkSession {
  id: string;
  organization_id: string;
  user_id: string;
  workstation_id: string;
  item_id: string | null;
  work_order_id: string | null;
  kiosk_user_id: string | null;
  auth_method: WorkAuthMethod;
  started_at: string;
  ended_at: string | null;
  end_reason: WorkEndReason | null;
  unit_started_at: string;
  units_completed: number;
  units_scrapped: number;
  last_activity_at: string;
  created_at: string;
  updated_at: string;
}

export type WorkBreakReason = 'break' | 'lunch' | 'waiting_parts' | 'machine_down' | 'meeting' | 'other';

export const BREAK_REASONS: Array<{ value: WorkBreakReason; label: string }> = [
  { value: 'break', label: 'Break' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'waiting_parts', label: 'Waiting on parts' },
  { value: 'machine_down', label: 'Machine / tool down' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'other', label: 'Something else' },
];

export interface WorkBreak {
  id: string;
  organization_id: string;
  session_id: string;
  reason: WorkBreakReason;
  started_at: string;
  ended_at: string | null;
  created_at: string;
}

export type WorkUnitOutcome = 'good' | 'scrap';

export interface WorkUnit {
  id: string;
  organization_id: string;
  session_id: string | null;
  user_id: string;
  /** Null when a scrap was logged from the scrap log without a station. */
  workstation_id: string | null;
  item_id: string | null;
  work_order_id: string | null;
  seq: number;
  outcome: WorkUnitOutcome;
  defect_id: string | null;
  started_at: string;
  completed_at: string;
  duration_seconds: number;
  break_seconds: number;
  created_at: string;
}

export type WorkDefectSeverity = 'minor' | 'major' | 'critical';
export type WorkDefectDisposition = 'scrap' | 'rework' | 'use_as_is' | 'hold';
export type WorkDefectStatus = 'open' | 'reviewed' | 'resolved';

export const DEFECT_SEVERITIES: Array<{ value: WorkDefectSeverity; label: string }> = [
  { value: 'minor', label: 'Minor' },
  { value: 'major', label: 'Major' },
  { value: 'critical', label: 'Critical' },
];

export const DEFECT_DISPOSITIONS: Array<{ value: WorkDefectDisposition; label: string }> = [
  { value: 'scrap', label: 'Scrap it' },
  { value: 'rework', label: 'Rework' },
  { value: 'hold', label: 'Hold for review' },
  { value: 'use_as_is', label: 'Use as-is' },
];

export interface ScrappedPart {
  item_id: string | null;
  sku: string;
  name: string;
  qty: number;
}

export interface WorkDefect {
  id: string;
  organization_id: string;
  session_id: string | null;
  user_id: string;
  workstation_id: string | null;
  item_id: string | null;
  step_id: string | null;
  step_number: number | null;
  progress_pct: number | null;
  description: string;
  severity: WorkDefectSeverity;
  disposition: WorkDefectDisposition;
  scrapped_parts: ScrappedPart[];
  photo_paths: string[];
  /** Scrap-type slugs. Required when disposition is scrap. */
  scrap_tags: string[];
  /** The scrapped unit should be reworked, not only thrown out. */
  needs_rework: boolean;
  rework_notes: string | null;
  /** Net seconds already spent on the unit when the report was saved. */
  committed_seconds: number | null;
  /** Thrown-out components at their current cost (latest PO landed cost, else supplier price). Null until costs are computed. */
  material_cost_cents: number | null;
  /** committed_seconds at the worker's pay rate. Null when no pay rate is on file. */
  labor_cost_cents: number | null;
  status: WorkDefectStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  nonconformance_id: string | null;
  created_at: string;
  updated_at: string;
}

export type WorkNoteKind = 'issue' | 'suggestion' | 'tool_request' | 'instruction_change' | 'safety' | 'kudos';
export type WorkNoteStatus = 'new' | 'acknowledged' | 'planned' | 'done' | 'declined';

export const NOTE_KINDS: Array<{ value: WorkNoteKind; label: string }> = [
  { value: 'issue', label: 'Something went wrong' },
  { value: 'suggestion', label: 'Idea to improve' },
  { value: 'tool_request', label: 'I need a tool' },
  { value: 'instruction_change', label: 'Fix the instructions' },
  { value: 'safety', label: 'Safety concern' },
  { value: 'kudos', label: 'Shout-out' },
];

export const NOTE_STATUSES: Array<{ value: WorkNoteStatus; label: string }> = [
  { value: 'new', label: 'New' },
  { value: 'acknowledged', label: 'Seen' },
  { value: 'planned', label: 'Planned' },
  { value: 'done', label: 'Done' },
  { value: 'declined', label: 'Not now' },
];

export interface WorkNote {
  id: string;
  organization_id: string;
  session_id: string | null;
  user_id: string;
  workstation_id: string | null;
  item_id: string | null;
  kind: WorkNoteKind;
  body: string;
  step_id: string | null;
  step_number: number | null;
  photo_paths: string[];
  status: WorkNoteStatus;
  manager_response: string | null;
  responded_by: string | null;
  responded_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkCheckin {
  id: string;
  organization_id: string;
  user_id: string;
  session_id: string | null;
  checkin_date: string;
  mood: 1 | 2 | 3 | 4 | 5;
  note: string | null;
  is_private: boolean;
  created_at: string;
  updated_at: string;
}

export const MOODS: Array<{ value: 1 | 2 | 3 | 4 | 5; label: string; emoji: string }> = [
  { value: 1, label: 'Rough', emoji: '😣' },
  { value: 2, label: 'Meh', emoji: '😕' },
  { value: 3, label: 'Okay', emoji: '😐' },
  { value: 4, label: 'Good', emoji: '🙂' },
  { value: 5, label: 'Great', emoji: '😄' },
];

export interface WorkInstruction {
  id: string;
  organization_id: string;
  item_id: string;
  workstation_id: string | null;
  title: string;
  summary: string | null;
  safety_notes: string | null;
  target_seconds: number | null;
  version: number;
  is_published: boolean;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkInstructionStep {
  id: string;
  organization_id: string;
  instruction_id: string;
  step_number: number;
  title: string;
  body: string | null;
  caution: string | null;
  image_path: string | null;
  est_seconds: number | null;
  created_at: string;
  updated_at: string;
}

export interface WorkInstructionWithSteps extends WorkInstruction {
  steps: WorkInstructionStep[];
}

/** One step inside a sign-off's copy of the instructions (migration 133). */
export interface SignoffInstructionStep {
  id: string;
  step_number: number;
  title: string;
  body: string | null;
  caution: string | null;
  /** The copied photo under `<org>/signoffs/<signoff id>/`, or the live photo when the copy failed. */
  image_path: string | null;
  source_image_path: string | null;
  est_seconds: number | null;
}

/** An instruction set as it stood when someone was signed off on it. */
export interface SignoffInstructionSet {
  instruction_id: string;
  item_id: string;
  item_sku: string | null;
  item_name: string | null;
  workstation_id: string | null;
  title: string;
  summary: string | null;
  safety_notes: string | null;
  target_seconds: number | null;
  version: number;
  updated_at: string;
  steps: SignoffInstructionStep[];
}

/** `workstation_signoffs` row without the instruction copy. */
export interface WorkstationSignoff {
  id: string;
  organization_id: string;
  /** Null once the station itself was deleted; `workstation_name` keeps the label. */
  workstation_id: string | null;
  workstation_name: string;
  user_id: string;
  signed_off_on: string;
  signed_off_by: string | null;
  notes: string | null;
  instructions_hash: string;
  revoked_at: string | null;
  revoked_by: string | null;
  revoke_reason: string | null;
  superseded_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkstationSignoffWithCopy extends WorkstationSignoff {
  instructions: SignoffInstructionSet[];
}

/** Return row of `work_station_instruction_hashes`. */
export interface StationInstructionHash {
  workstation_id: string;
  instructions_hash: string;
  set_count: number;
  step_count: number;
}

/** `workstations` row after migrations 116 and 144. */
export interface FloorWorkstation {
  id: string;
  organization_id: string;
  name: string;
  position: number;
  description: string | null;
  is_active: boolean;
  floor_x: number | null;
  floor_y: number | null;
  default_item_id: string | null;
  target_seconds: number | null;
  /** Storage key in `work-instructions` of the station's hero photo. */
  cover_image_path: string | null;
  location_notes: string | null;
  safety_notes: string | null;
  /** Required PPE, one entry per item. */
  ppe: string[];
  setup_notes: string | null;
  created_at: string;
  updated_at: string;
}

/** `workstation_photos` row (migration 144). */
export interface WorkstationPhoto {
  id: string;
  organization_id: string;
  workstation_id: string;
  image_path: string;
  caption: string | null;
  position: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** `workstation_tools` row (migration 144). */
export interface WorkstationTool {
  id: string;
  organization_id: string;
  workstation_id: string;
  name: string;
  equipment_id: string | null;
  quantity: number;
  notes: string | null;
  is_required: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

/** `workstation_parts` row (migration 144). */
export interface WorkstationPart {
  id: string;
  organization_id: string;
  workstation_id: string;
  item_id: string | null;
  name: string;
  quantity: number | null;
  uom: string | null;
  bin_location: string | null;
  notes: string | null;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface WorkCredentialStatus {
  user_id: string;
  has_pin: boolean;
  has_badge: boolean;
  badge_last4: string | null;
  locked_until: string | null;
  pin_set_at: string | null;
  badge_set_at: string | null;
}

/** Return shape of `work_session_snapshot` (and the mutating RPCs). */
export interface WorkSessionSnapshot {
  session: WorkSession;
  open_break: WorkBreak | null;
  units_today: number;
  avg_seconds: number | null;
  best_seconds: number | null;
  last_unit: WorkUnit | null;
  unit?: WorkUnit;
  defect?: WorkDefect;
  /** Present after work_unit_complete_serialized (migration 142). */
  serialized_unit?: { id: string; serial_number: string; status: string } & Record<string, unknown>;
}

export type WorkSessionStartResult =
  | { ok: true; session: WorkSession; needs_checkin: boolean }
  | { ok: false; error: string; attempts_left?: number; locked_until?: string };

export interface WorkShiftSummary {
  session: WorkSession;
  units: number;
  scrapped: number;
  avg_seconds: number | null;
  best_seconds: number | null;
  worked_seconds: number;
  break_seconds: number;
  defects: number;
  notes: number;
}

/** Return shape of `work_profile_stats`. */
export interface WorkProfileStats {
  user_id: string;
  units_today: number;
  units_7d: number;
  units_30d: number;
  units_total: number;
  scrap_total: number;
  scrap_30d: number;
  defects_reported: number;
  notes_total: number;
  suggestions_total: number;
  suggestions_adopted: number;
  safety_total: number;
  kudos_total: number;
  checkins_total: number;
  checkin_streak: number;
  avg_mood_30d: number | null;
  sessions_total: number;
  days_active: number;
  seconds_worked_total: number;
  avg_cycle_seconds_30d: number | null;
  best_cycle_seconds: number | null;
  best_day_units: number;
  first_session_at: string | null;
  stations_worked: number;
}

export interface WorkLeaderboardRow {
  user_id: string;
  units: number;
  scrapped: number;
  defects_reported: number;
  notes: number;
  safety_notes: number;
  suggestions: number;
  checkins: number;
  seconds_worked: number;
  avg_cycle_seconds: number | null;
}

export interface WorkMoraleDay {
  day: string;
  responses: number;
  avg_mood: number;
  low_count: number;
}

export interface WorkStationDay {
  day: string;
  workstation_id: string;
  units: number;
  scrapped: number;
  avg_cycle_seconds: number | null;
  workers: number;
}
